/* eslint-disable camelcase */
import * as React from 'react';
import {useState, useMemo, useRef} from 'react';
import {createViewConfig, view, assignProps} from '@sail/engine';
import {
  FormFieldGroup,
  TextField,
  SearchField,
  Select,
  CONNECT_ONLY_defaultCountryLabels as defaultCountryLabels,
  CONNECT_ONLY_addressSchemasJSON as addressSchemasJSON,
  addressFieldLabels as defaultFieldLabels,
  tokens,
} from '@sail/ui';
import type {
  View,
  AddressFieldSchema,
  FieldLabelOverrides,
  AddressFieldType,
} from '@sail/ui';

export type AddressFieldCountryCode = keyof typeof addressSchemasJSON;

function deepMerge<T>(target: T, source: Partial<T>): T {
  const result = {...target} as T;
  const keys = Object.keys(source);
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i] as keyof T;
    const value = source[key] as T[keyof T];
    if (typeof value === 'object' && typeof target[key] === 'object') {
      result[key] = deepMerge(target[key], value);
    } else {
      result[key] = value;
    }
  }
  return result;
}

const addressSchemas = addressSchemasJSON as Record<
  AddressFieldCountryCode,
  AddressFieldSchema
>;

const COUNTRIES = Object.keys(addressSchemas) as AddressFieldCountryCode[];

export type AddressFieldsIncludingOverrides<T> = {
  /**
   * A two-letter international country code (ISO 3166-1 alpha-2), e.g. "US"
   */
  countryCode: T;

  /**
   * A street address which fully identifies the location of an address within its district
   */
  address: T;

  /**
   * An optional second line of a street address
   */
  address2: T;

  /**
   * An optional third line of a street address. This field is not present in the default address schema. It can be only used if provided through schemaOverride prop.
   */
  address3: T;

  /**
   * An optional fourth line of a street address. This field is not present in the default address schema. It can be only used if provided through schemaOverride prop.
   */
  address4: T;

  /**
   * State, county, or province
   */
  subregion: T;

  /**
   * City, district, suburb, town, or village
   */
  locality: T;

  /**
   * Neighborhood, township, district, or suburb
   */
  dependentLocality: T;

  /**
   * A postal code, such as ZIP in the United States
   */
  postalCode: T;

  /**
   * A code used to route or disambiguate addresses within a locality
   */
  sortingCode: T;
};

/**
 * Includes default address keys + keys that can be provided through schema overrides (Except for country)
 */
const addressFieldKeysArr: Array<
  keyof AddressFieldsIncludingOverrides<string>
> = [
  'address',
  'address2',
  'address3',
  'address4',
  'subregion',
  'locality',
  'dependentLocality',
  'postalCode',
  'sortingCode',
];

/**
 * The internal representation of address field values.
 * All fields except `countryCode` are optional, though certain country schemas may
 * call for runtime validation to ensure presence of certain fields.
 */
export type AddressValueIncludingOverrides = {
  countryCode: AddressFieldCountryCode;
} & Partial<Omit<AddressFieldsIncludingOverrides<string>, 'countryCode'>>;

/**
 * This interface provides a way to transform field values between the `AddressValue` interface
 * and a custom interface that aligns with an external data model.
 * The `unsupportedFields` property can be used to omit fields that aren't represented
 * by the external data model.
 */
export type AddressValueAdapter<T> = {
  /**
   * A function that transforms the provided value type into the expected internal representation.
   */
  toAddressValue: (value: T) => AddressValueIncludingOverrides;
  /**
   * A function that transforms the value's internal representation back into the provided value type.
   */
  fromAddressValue: (value: AddressValueIncludingOverrides) => T;
  /**
   * A list of field types not supported by the data model.
   * Including a field type in this list will prevent it from being shown.
   */
  unsupportedFields?: (keyof AddressFieldsIncludingOverrides<string>)[];
};

export type AddressAutocompleteItem = {
  address: string;
  addressValue: AddressValueIncludingOverrides;
};

const DEFAULT_ADAPTER: AddressValueAdapter<AddressValueIncludingOverrides> = {
  toAddressValue: (value: AddressValueIncludingOverrides) => value,
  fromAddressValue: (value: AddressValueIncludingOverrides) => value,
};

export type SubFieldComponentType = 'Text' | 'Select';

export type DropdownOption = {
  key: string;
  name: string;
};

export type SchemaConfig = {
  type: keyof AddressFieldsIncludingOverrides<string>;
  hide: boolean;
  label: string;
  component?: SubFieldComponentType;
  options?: Array<DropdownOption>;
  disabled?: boolean;
  error?: string;
  handleBlur?: () => void;
};

export type SchemaOverride = {
  /**
   * Configuration for overriding the default address schema.
   *
   * If provided, this configuration will be used by Sail to render the address fields
   * instead of the default schema defined in `addressSchemasJSON`.
   */
  config: Array<SchemaConfig>;
  /**
   * Specifies the order of address subfields.
   *
   * The fields listed in this array will be rendered in the specified order.
   * Any subfields not included in this array will be rendered in their default
   * order after the fields specified here.
   */
  order?: Array<keyof AddressFieldsIncludingOverrides<string>>;
};

type AddressFieldProps<T = AddressValueIncludingOverrides> = Omit<
  View.IntrinsicElement<'fieldset'>,
  'defaultValue' | 'onChange'
> & {
  /**
   * The set of values for the corresponding fields when being used as a controlled component; used in conjunction with `onChange`
   */
  value?: T;

  /**
   * The set of values the component should be initialized with when being used as an uncontrolled component
   */
  defaultValue?: T;

  /**
   * The callback function to fire when the value changes
   */
  onChange?: (value: T) => void;

  /**
   * An adapter to transform the value type to and from the expected internal type to one compatible with another data model
   */
  adapter: AddressValueAdapter<T>;

  /**
   * A list of two-letter international country codes (ISO 3166-1 alpha-2, e.g. "US") that are selectable from the country selector
   */
  countryAllowlist: AddressFieldCountryCode[];

  /**
   * A list of two-letter international country codes (ISO 3166-1 alpha-2, e.g. "US") that should be excluded from the country selector.
   * Note that this will override `countryAllowlist` if there's a conflict.
   */
  countryDenylist: AddressFieldCountryCode[];

  /**
   * Text that describes the control. Will be both visible and clickable.
   */
  label?: React.ReactNode;

  /**
   * Descriptive text that will be rendered adjacent to the control's label
   */
  description?: React.ReactNode;

  /**
   * Error text that will be rendered above the control
   */
  error?: React.ReactNode;

  /**
   * Disables the field and removes it from submission
   */
  disabled?: boolean;

  /**
   * Makes the field non-editable
   */
  readOnly?: boolean;

  /**
   * Enable or disable default browser autocomplete. Defaults to true.
   */
  autoComplete: boolean;

  /**
   * Sets the size of the field
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Hides the country selector when only 1 country is valid
   */
  preferHideCountrySelector?: boolean;

  /**
   * A function for retrieving a list of address for autocompletion
   */
  onAutocomplete?: (value: string) => Promise<Array<AddressAutocompleteItem>>;

  /**
   * Custom labels, useful for localization. API subject to change!
   */
  unstable_labelsOverrides: {
    address?: FieldLabelOverrides;
    country?: Partial<typeof defaultCountryLabels>;
  };

  subviews?: View.Subviews<{
    countryCode: typeof Select;
    address1: typeof SearchField<AddressAutocompleteItem & {id: string}>;
    address2: typeof TextField;
    subregion: typeof Select;
    locality: typeof TextField;
    dependentLocality: typeof TextField;
    postalCode: typeof TextField;
    sortingCode: typeof TextField;
  }>;

  schemaOverride?: SchemaOverride;
};

function filterValues<T extends Record<string, string>>(
  obj: T,
  callback: (key: keyof T, obj: T) => boolean,
): T {
  return (Object.keys(obj) as (keyof T)[]).reduce((memo, key) => {
    if (callback(key, obj)) {
      memo[key] = obj[key];
    }
    return memo;
  }, {} as T);
}

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const AddressFieldConfig = createViewConfig({
  // we need to be able to accept an arbitrary type for the adapter
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: {} as AddressFieldProps<any>,
  defaults: {
    countryAllowlist: COUNTRIES,
    countryDenylist: [],
    adapter: DEFAULT_ADAPTER,
    autoComplete: true,
    preferHideCountrySelector: false,
    unstable_labelsOverrides: {},
  },
  flattens: true,
});

export const SailCustomisableAddressField =
  AddressFieldConfig.createGenericView(function <
    T extends Record<string, string>,
  >({
    label,
    value: valueProp,
    defaultValue,
    onChange,
    countryAllowlist,
    countryDenylist,
    adapter,
    readOnly,
    autoComplete,
    preferHideCountrySelector,
    onAutocomplete,
    unstable_labelsOverrides,
    subviews,
    schemaOverride,
    ...props
  }: View.RenderProps<AddressFieldProps<T>>) {
    const {
      country: countryLabelsOverrides = {},
      address: addressLabelsOverrides = {},
    } = unstable_labelsOverrides;

    const countryLabels = useMemo(
      () => ({...defaultCountryLabels, ...countryLabelsOverrides}),
      [countryLabelsOverrides],
    );
    const countries = useMemo(
      () =>
        countryAllowlist
          .slice()
          .filter((country) => !countryDenylist.includes(country))
          .sort((a, b) => countryLabels[a].localeCompare(countryLabels[b])),
      [countryAllowlist, countryDenylist, countryLabels],
    );

    const [uncontrolledValue, setUncontrolledValue] = useState<T>(
      defaultValue ?? {
        // @ts-expect-error 'countryCode' does not exist in type '() => T'
        countryCode: countries.length === 1 ? countries[0] : 'US',
      },
    );

    const addressValue = useMemo(() => {
      const adaptedValue = adapter.toAddressValue(
        valueProp ?? uncontrolledValue,
      );
      return {
        ...adaptedValue,
        // ensure that countryCode is always set
        countryCode:
          adaptedValue.countryCode ??
          valueProp?.countryCode ??
          uncontrolledValue?.countryCode,
      };
    }, [adapter, valueProp, uncontrolledValue]);

    const {countryCode} = addressValue;

    const schema =
      useMemo(() => addressSchemas[countryCode], [countryCode]) ?? [];

    const unsupportedFields = new Set(adapter.unsupportedFields);

    const updateValue = React.useCallback(
      (value: T) => {
        const filteredValue = filterValues(value, (key, obj) =>
          Boolean(obj[key]),
        );

        onChange?.(filteredValue);
        setUncontrolledValue(filteredValue);
      },
      [onChange],
    );

    const [autocompleteItems, setAutocompleteItems] = useState<
      Array<AddressAutocompleteItem & {id: string}>
    >([]);
    const latestAutocompletePromise = useRef<Promise<
      Array<AddressAutocompleteItem>
    > | null>(null);
    const line1Focused = useRef<boolean>(false);
    const [isAutocompleteOpen, setAutocompleteOpen] = useState(false);

    function clearAutocompleteRequest() {
      latestAutocompletePromise.current = null;
    }

    async function runAutocomplete(value: string) {
      if (
        onAutocomplete &&
        // We only want to run this if the input is still focused. The input value
        // can change without being focused due to browser extension autocompletions,
        // and we don't want to show our autocompletions if that happens
        line1Focused.current
      ) {
        if (value === '') {
          clearAutocompleteRequest();
          setAutocompleteItems([]);
          setAutocompleteOpen(false);
        } else {
          const promise = onAutocomplete(value);
          latestAutocompletePromise.current = promise;
          const items = await promise;

          // Only ever use the results from the latest request
          if (latestAutocompletePromise.current === promise) {
            setAutocompleteItems(
              items.map((item) => ({id: item.address, ...item})),
            );
            setAutocompleteOpen(items.length > 0);
          }
        }
      }
    }

    if (!schema) {
      throw new Error(
        `No address schema found for country code "${countryCode}"`,
      );
    }
    const showCountrySelector = useMemo(
      () => !preferHideCountrySelector || countries.length > 1,
      [countries.length, preferHideCountrySelector],
    );

    const addressFieldLabels = deepMerge(
      defaultFieldLabels,
      addressLabelsOverrides as Partial<typeof defaultFieldLabels>,
    );

    const getSchemaField = (
      addressFieldKey: keyof AddressFieldsIncludingOverrides<string>,
    ) => {
      const field = schema.find(
        (field) =>
          field.type === addressFieldKey ||
          (field.type === 'dependent-locality' &&
            addressFieldKey === 'dependentLocality') ||
          (field.type === 'sorting-code' &&
            addressFieldKey === 'sortingCode') ||
          (field.type === 'postal-code' && addressFieldKey === 'postalCode'),
      );
      return field;
    };

    const getDefaultComponent = (
      field: AddressFieldType | undefined,
      index = 0,
    ) => {
      // Fields like address3, address4 are not present in addressSchemasJSON and are only present in schemaOverride
      // For these fields, the getDefaultComponent function should return null as they are not present in the default address rendering logic
      if (!field) {
        return null;
      }
      if (field.type === 'address' && !unsupportedFields.has('address')) {
        return (
          <React.Fragment key="address">
            <SearchField
              inherits={subviews.address1}
              data-testid="address1"
              key="address1"
              label={addressFieldLabels.address_line1}
              placeholder={addressFieldLabels.address_line1}
              value={addressValue.address ?? ''}
              defaultFilter={false}
              // There is never a selected value. We fully control the value of this field
              // which is just a raw text value
              selectedKey={null}
              data={autocompleteItems}
              item="address"
              onInputChange={(address: string) => {
                updateValue(
                  adapter.fromAddressValue({
                    ...addressValue,
                    address,
                  }),
                );
                runAutocomplete(address);
              }}
              isOpen={isAutocompleteOpen}
              onOpenChange={(open: any) => {
                // We never want SearchField to open it, we only want to respect
                // its close behaviors
                if (!open) {
                  setAutocompleteOpen(open);
                }
              }}
              onSelectionChange={(id: any) => {
                const item = autocompleteItems?.find((s) => s.id === id);
                if (item) {
                  updateValue(adapter.fromAddressValue(item.addressValue));
                }
                setAutocompleteOpen(false);
              }}
              hiddenElements={['label']}
              readOnly={readOnly}
              required={field.required}
              subviews={{
                input: {
                  uses: [
                    assignProps({
                      type: 'text',
                      autoComplete:
                        autoComplete === true ? 'address-line1' : 'off',
                      onFocus: () => {
                        line1Focused.current = true;
                      },
                      onBlur: () => {
                        line1Focused.current = false;
                        clearAutocompleteRequest();
                      },
                    }),
                  ],
                },
                searchIcon: {css: {display: 'none'}},
                cancelAction: {css: {display: 'none'}},
              }}
            />
            <TextField
              data-testid="address2"
              inherits={subviews.address2}
              key="address2"
              label={addressFieldLabels.address_line2}
              placeholder={addressFieldLabels.address_line2}
              value={addressValue.address2 ?? ''}
              onChange={(e) => {
                const address2 = e.target.value;
                updateValue(
                  adapter.fromAddressValue({
                    ...addressValue,
                    address2,
                  }),
                );
              }}
              hiddenElements={['label']}
              readOnly={readOnly}
              autoComplete={autoComplete === true ? 'address-line2' : 'off'}
            />
          </React.Fragment>
        );
      } else if (
        field.type === 'subregion' &&
        !unsupportedFields.has('subregion') &&
        field.schema.list.length
      ) {
        return (
          <Select
            data-testid={field.type}
            inherits={subviews.subregion}
            key={field.type}
            label={addressFieldLabels.subregion[field.schema.nameType]}
            placeholder={addressFieldLabels.subregion[field.schema.nameType]}
            value={addressValue.subregion ?? ''}
            onChange={(e) => {
              const subregion = e.target.value;
              updateValue(
                adapter.fromAddressValue({
                  ...addressValue,
                  subregion,
                }),
              );
            }}
            hiddenElements={['label']}
            disabled={readOnly}
            required={field.required}
            autoComplete={autoComplete === true ? 'address-level1' : 'off'}
          >
            <view.option value="" css={{display: 'none'}}>
              {addressFieldLabels.subregion[field.schema.nameType]}
            </view.option>
            {field.schema.list.map((subregion) => (
              <option key={subregion.key} value={subregion.key}>
                {subregion.latinName
                  ? `${subregion.name} - ${subregion.latinName}`
                  : subregion.name}
              </option>
            ))}
          </Select>
        );
      } else if (
        field.type === 'locality' &&
        !unsupportedFields.has('locality')
      ) {
        return (
          <TextField
            data-testid={field.type}
            inherits={subviews.locality}
            key={field.type}
            label={addressFieldLabels.locality[field.schema.nameType]}
            placeholder={addressFieldLabels.locality[field.schema.nameType]}
            value={addressValue.locality ?? ''}
            onChange={(e) => {
              const locality = e.target.value;
              updateValue(
                adapter.fromAddressValue({
                  ...addressValue,
                  locality,
                }),
              );
            }}
            hiddenElements={['label']}
            readOnly={readOnly}
            required={field.required}
            autoComplete={autoComplete === true ? 'address-level2' : 'off'}
          />
        );
      } else if (
        field.type === 'dependent-locality' &&
        !unsupportedFields.has('dependentLocality')
      ) {
        return (
          <TextField
            inherits={subviews.dependentLocality}
            key={field.type}
            data-testid={field.type}
            label={addressFieldLabels.dependentLocality[field.schema.nameType]}
            placeholder={
              addressFieldLabels.dependentLocality[field.schema.nameType]
            }
            value={addressValue.dependentLocality ?? ''}
            onChange={(e) => {
              const dependentLocality = e.target.value;
              updateValue(
                adapter.fromAddressValue({
                  ...addressValue,
                  dependentLocality,
                }),
              );
            }}
            hiddenElements={['label']}
            readOnly={readOnly}
            required={field.required}
            autoComplete={autoComplete === true ? 'address-level3' : 'off'}
          />
        );
      } else if (
        field.type === 'postal-code' &&
        !unsupportedFields.has('postalCode')
      ) {
        return (
          <TextField
            data-testid={field.type}
            inherits={subviews.postalCode}
            key={field.type}
            label={addressFieldLabels.postalCode[field.schema.nameType]}
            placeholder={addressFieldLabels.postalCode[field.schema.nameType]}
            value={addressValue.postalCode ?? ''}
            onChange={(e) => {
              const postalCode = e.target.value;
              updateValue(
                adapter.fromAddressValue({
                  ...addressValue,
                  postalCode,
                }),
              );
            }}
            hiddenElements={['label']}
            readOnly={readOnly}
            required={field.required}
            autoComplete={autoComplete === true ? 'postal-code' : 'off'}
          />
        );
      } else if (
        field.type === 'sorting-code' &&
        !unsupportedFields.has('sortingCode')
      ) {
        return (
          <TextField
            data-testid={field.type + index}
            inherits={subviews.sortingCode}
            // Cote d'Ivoire has two sorting code fields
            // so we need to differentiate their keys with an index
            // eslint-disable-next-line react/no-array-index-key
            key={field.type + index}
            label={addressFieldLabels.sortingCode[field.schema.nameType]}
            placeholder={addressFieldLabels.sortingCode[field.schema.nameType]}
            value={addressValue.sortingCode ?? ''}
            onChange={(e) => {
              const sortingCode = e.target.value;
              updateValue(
                adapter.fromAddressValue({
                  ...addressValue,
                  sortingCode,
                }),
              );
            }}
            hiddenElements={['label']}
            readOnly={readOnly}
            required={field.required}
          />
        );
      }

      return null;
    };

    const getSubfieldsOrder = () => {
      const orderOverride = schemaOverride?.order;
      if (orderOverride) {
        const addressFieldKeysArrExcludingOrderOverride =
          addressFieldKeysArr.filter((field) => !orderOverride.includes(field));
        return [...orderOverride, ...addressFieldKeysArrExcludingOrderOverride];
      }
      return addressFieldKeysArr;
    };

    React.useEffect(() => {
      if (!schemaOverride) {
        return;
      }
      // autoselect dropdown option if there is only one option available
      schemaOverride.config.forEach((config) => {
        const addressFieldKey = config.type;
        if (
          config.component === 'Select' &&
          !addressValue?.[addressFieldKey] &&
          !config.disabled &&
          config.options &&
          config.options.length === 1
        ) {
          updateValue(
            adapter.fromAddressValue({
              ...addressValue,
              [addressFieldKey]: config.options[0].key,
            }),
          );
        }
      });
    }, [adapter, addressValue, updateValue, schemaOverride]);

    const renderAddressComponents = () => {
      if (!schemaOverride) {
        // Maintain order of fields as mentioned in schema file
        return schema.map((field, index) => {
          return getDefaultComponent(field, index);
        });
      }

      return getSubfieldsOrder().map((addressFieldKey) => {
        const schemaOverrideConfig = schemaOverride.config.find(
          (config) => config.type === addressFieldKey,
        );

        if (!schemaOverrideConfig) {
          const field = getSchemaField(addressFieldKey);
          return getDefaultComponent(field);
        }

        if (schemaOverrideConfig.hide) {
          return null;
        }

        const dropdownColor = (
          addressValue[addressFieldKey]
            ? tokens.color.text
            : tokens.color.text.form.placeholder
        ) as any;

        if (schemaOverrideConfig.component === 'Select') {
          return (
            <Select
              data-testid={addressFieldKey}
              key={addressFieldKey}
              label={schemaOverrideConfig.label}
              placeholder={schemaOverrideConfig.label}
              value={addressValue[addressFieldKey] ?? ''}
              onChange={(e) => {
                const {value} = e.target;
                updateValue(
                  adapter.fromAddressValue({
                    ...addressValue,
                    [addressFieldKey]: value,
                  }),
                );
              }}
              hiddenElements={['label']}
              disabled={schemaOverrideConfig.disabled}
              error={schemaOverrideConfig.error}
              style={{color: dropdownColor, fontWeight: 'normal'}}
            >
              <view.option value="" css={{display: 'none'}}>
                {schemaOverrideConfig.label}
              </view.option>
              {schemaOverrideConfig.options?.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.name}
                </option>
              ))}
            </Select>
          );
        }

        return (
          <TextField
            data-testid={addressFieldKey}
            key={addressFieldKey}
            label={schemaOverrideConfig.label}
            placeholder={schemaOverrideConfig.label}
            value={addressValue[addressFieldKey] ?? ''}
            disabled={schemaOverrideConfig.disabled}
            onChange={(e) => {
              const {value} = e.target;
              updateValue(
                adapter.fromAddressValue({
                  ...addressValue,
                  [addressFieldKey]: value,
                }),
              );
            }}
            hiddenElements={['label']}
            error={schemaOverrideConfig.error}
            onBlur={schemaOverrideConfig.handleBlur}
          />
        );
      });
    };

    return (
      <FormFieldGroup {...props} legend={label} layout="vertical">
        {showCountrySelector ? (
          <Select
            data-testid="country"
            inherits={subviews.countryCode}
            label={addressFieldLabels.country}
            value={countryCode}
            onChange={(e) => {
              const nextAddressFieldCountryCode = e.target
                .value as AddressFieldCountryCode;
              const nextAddressValue: AddressValueIncludingOverrides = {
                countryCode: nextAddressFieldCountryCode,
              };
              updateValue(adapter.fromAddressValue(nextAddressValue));
            }}
            hiddenElements={['label']}
            disabled={countries.length <= 1 || readOnly}
            required
            autoComplete={autoComplete === true ? 'country' : 'off'}
          >
            <view.option value="" css={{display: 'none'}}>
              {addressFieldLabels.country}
            </view.option>
            {!countries.includes(countryCode) ? (
              <option key={countryCode} value={countryCode} disabled>
                {countryLabels[countryCode]}
              </option>
            ) : null}
            {countries.map((countryCode) => (
              <option key={countryCode} value={countryCode}>
                {countryLabels[countryCode]}
              </option>
            ))}
          </Select>
        ) : null}

        {renderAddressComponents()}
        {Object.entries(uncontrolledValue).map(([name, value]) =>
          value ? (
            <input
              data-testid={name}
              key={name}
              type="hidden"
              name={name}
              value={value}
            />
          ) : null,
        )}
      </FormFieldGroup>
    );
  });
